import { useState, useEffect, useCallback } from "react";
import { useConfirm } from "context/ConfirmContext";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import LoadingSpinner from "components/LoadingSpinner";
import {
  listRelatedParties,
  buildRelatedPartyGraph,
  addManualRelatedParty,
  updateRelatedParty,
  deleteRelatedParty,
  listRelatedPartySuggestions,
  updateRelatedPartySuggestion,
  mergeRelatedParties,
} from "services/relatedPartyService";
import { TC, FS, FW } from "../../tokens/text";

// ─── Constants ─────────────────────────────────────────────────────────────────

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    color: TC.primary,
    fontSize: FS.md,
    "& fieldset": { bordercolor: "#7a94b0" },
    "&:hover fieldset": { bordercolor: "#4a6080" },
    "&.Mui-focused fieldset": { borderColor: "#0075ff" },
    background: "#f4f8ff",
    borderRadius: "8px",
  },
  "& .MuiInputLabel-root": { color: TC.secondary, fontSize: "0.8rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: TC.link },
  "& .MuiSelect-icon": { color: TC.secondary },
  // Prevent MUI's hidden measurement textarea from triggering a scrollbar
  "& textarea[aria-hidden]": { overflow: "hidden" },
};

const STATUS_CONFIG = {
  pending:   { bg: "rgba(255,152,0,0.14)",   border: "rgba(255,152,0,0.45)",   text: "#ffb74d", dot: "#ff9800", label: "Pending Review" },
  confirmed: { bg: "rgba(76,175,80,0.14)",   border: "rgba(76,175,80,0.4)",    text: "#81c784", dot: "#4caf50", label: "Confirmed" },
  dismissed: { bg: "rgba(120,120,140,0.10)", border: "rgba(120,120,140,0.25)", text: "#9e9e9e", dot: "#757575", label: "Dismissed" },
};

const SOURCE_CONFIG = {
  companies_house: { bg: "rgba(0,117,255,0.13)", border: "rgba(0,117,255,0.3)",   text: "#90caf9",  label: "Companies House", icon: "🏛" },
  user_input:      { bg: "rgba(156,39,176,0.13)", border: "rgba(156,39,176,0.35)", text: "#ce93d8", label: "Manual",           icon: "✏️" },
  inferred:        { bg: "rgba(255,235,59,0.11)", border: "rgba(255,235,59,0.3)",  text: "#fff176",  label: "AI-Inferred",     icon: "🤖" },
};

// Per-relationship-type icon and colour
const REL_CONFIG = {
  director:              { icon: "👤", label: "Director",               color: TC.link },
  psc:                   { icon: "⭐", label: "PSC",                    color: "#ffd54f" },
  close_family:          { icon: "🧑‍🤝‍🧑", label: "Close Family",          color: "#f48fb1" },
  shadow_director:       { icon: "👁",  label: "Shadow Director",        color: "#ce93d8" },
  ultimate_controlling:  { icon: "🔱", label: "Ultimate Controller",     color: "#ff8a65" },
  parent_or_controlling: { icon: "🏛",  label: "Parent / Controller",    color: "#a5d6a7" },
  director_controlled:   { icon: "🔗", label: "Director-Controlled Co.", color: TC.link },
  subsidiary_of:         { icon: "🏢", label: "Subsidiary",              color: "#80cbc4" },
  fellow_subsidiary:     { icon: "↔️", label: "Fellow Subsidiary",        color: "#b39ddb" },
  trust_partnership:     { icon: "🤝", label: "Trust / Partnership",     color: "#ffcc80" },
  informal_control:      { icon: "🔒", label: "Informal Control",        color: "#ef9a9a" },
  other:                 { icon: "📎", label: "Other",                   color: "#eeeeee" },
};

// Groups for the People sub-sections
const PEOPLE_GROUPS = [
  { key: "director",             label: "Directors" },
  { key: "psc",                  label: "Persons with Significant Control (PSC)" },
  { key: "shadow_director",      label: "Shadow Directors" },
  { key: "ultimate_controlling", label: "Ultimate Controlling Parties" },
  { key: "close_family",         label: "Close Family Members" },
];

// Groups for the Companies sub-sections
const COMPANY_GROUPS = [
  { key: "parent_or_controlling", label: "Parent & Controlling Entities" },
  { key: "subsidiary_of",         label: "Subsidiaries" },
  { key: "fellow_subsidiary",     label: "Fellow Subsidiaries" },
  { key: "director_controlled",   label: "Director / PSC Controlled Companies" },
  { key: "trust_partnership",     label: "Trusts & Partnerships" },
  { key: "informal_control",      label: "Informal Control" },
  { key: "other",                 label: "Other Related Entities" },
];

// Relationship type options for "Add Manually" dialog, split by entity type
const PERSON_REL_OPTIONS = [
  { value: "close_family",          label: "Close Family Member" },
  { value: "shadow_director",       label: "Shadow Director" },
  { value: "ultimate_controlling",  label: "Ultimate Controlling Party (person)" },
  { value: "psc",                   label: "PSC (not on CH)" },
  { value: "other",                 label: "Other" },
];

const COMPANY_REL_OPTIONS = [
  { value: "parent_or_controlling", label: "Parent / Controlling Entity" },
  { value: "subsidiary_of",         label: "Subsidiary" },
  { value: "fellow_subsidiary",     label: "Fellow Subsidiary" },
  { value: "director_controlled",   label: "Director-Controlled Company" },
  { value: "trust_partnership",     label: "Trust / Partnership" },
  { value: "informal_control",      label: "Informal Control (overseas / unregistered)" },
  { value: "ultimate_controlling",  label: "Ultimate Controlling Entity" },
  { value: "other",                 label: "Other" },
];

// ─── Small presentational chips ────────────────────────────────────────────────

function StatusBadge({ status }) {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <Chip
      label={
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.dot, display: "inline-block" }} />
          {c.label}
        </span>
      }
      size="small"
      sx={{ bgcolor: c.bg, color: c.text, border: `1px solid ${c.border}`, fontSize: "0.62rem", height: 20, fontWeight: FW.bold }}
    />
  );
}

function SourceBadge({ source }) {
  const c = SOURCE_CONFIG[source] || SOURCE_CONFIG.user_input;
  return (
    <Chip
      label={`${c.icon} ${c.label}`}
      size="small"
      sx={{ bgcolor: c.bg, color: c.text, border: `1px solid ${c.border}`, fontSize: "0.6rem", height: 18 }}
    />
  );
}

function RelChip({ relType }) {
  const c = REL_CONFIG[relType] || REL_CONFIG.other;
  return (
    <Chip
      label={`${c.icon} ${c.label}`}
      size="small"
      sx={{
        bgcolor: "#7a94b0",
        color: c.color,
        border: `1px solid ${c.color}30`,
        fontSize: "0.62rem",
        height: 20,
        fontWeight: FW.semibold,
      }}
    />
  );
}

// ─── Entity type avatar ────────────────────────────────────────────────────────

function EntityAvatar({ item }) {
  // Pick icon based on the most significant relationship type
  const primaryRel = item.relationshipTypes?.[0];
  const relCfg = REL_CONFIG[primaryRel];
  const isGroup = ["parent_or_controlling", "subsidiary_of", "fellow_subsidiary"].includes(primaryRel);

  const icon = relCfg?.icon || (item.entityType === "person" ? "👤" : "🏢");
  const bg   = item.entityType === "person"
    ? "rgba(0,117,255,0.12)"
    : isGroup
      ? "rgba(76,175,80,0.12)"
      : "rgba(255,152,0,0.10)";
  const border = item.entityType === "person"
    ? "1px solid rgba(0,117,255,0.25)"
    : isGroup
      ? "1px solid rgba(76,175,80,0.25)"
      : "1px solid rgba(255,152,0,0.2)";

  return (
    <VuiBox
      sx={{
        width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
        background: bg, border,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "1.1rem",
      }}
    >
      {icon}
    </VuiBox>
  );
}

// ─── Single row ────────────────────────────────────────────────────────────────

function RelatedPartyRow({ item, onConfirm, onDismiss, onDelete, onEditNotes, isSaving }) {
  const isManual    = item.source === "user_input";
  const isDismissed = item.confirmedStatus === "dismissed";
  const isConfirmed = item.confirmedStatus === "confirmed";

  return (
    <VuiBox
      display="flex"
      alignItems="flex-start"
      gap={1.5}
      px={2}
      py={1.5}
      sx={{
        borderBottom: "1px solid #e4eef8",
        opacity: isDismissed ? 0.45 : 1,
        transition: "opacity 0.2s, background 0.15s",
        "&:last-child": { borderBottom: "none" },
        "&:hover": { background: "#f4f8ff" },
      }}
    >
      <EntityAvatar item={item} />

      {/* Main content */}
      <VuiBox flex={1} minWidth={0}>
        {/* Name row */}
        <VuiBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.4}>
          <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary,  fontSize: "0.84rem", lineHeight: 1.3 }}>
            {item.name}
          </VuiTypography>
          {item.metadata?.isKnownClient && (
            <Chip
              label="⭐ Also a Client"
              size="small"
              sx={{ bgcolor: "rgba(255,235,59,0.14)", color: "#fff176", border: "1px solid rgba(255,235,59,0.3)", fontSize: "0.6rem", height: 18, fontWeight: FW.bold }}
            />
          )}
          {!item.active && (
            <Chip label="Inactive" size="small"
              sx={{ bgcolor: "rgba(120,120,140,0.1)", color: "#757575", border: "1px solid rgba(120,120,140,0.2)", fontSize: "0.58rem", height: 16 }} />
          )}
        </VuiBox>

        {/* Relationship chips */}
        {item.relationshipTypes?.length > 0 && (
          <VuiBox display="flex" gap={0.5} flexWrap="wrap" mb={0.6}>
            {item.relationshipTypes.map((t) => <RelChip key={t} relType={t} />)}
          </VuiBox>
        )}

        {/* Why related */}
        <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: "0.73rem", lineHeight: 1.5, opacity: 0.85, display: "block" }}>
          {item.reason}
        </VuiTypography>

        {/* Notes */}
        {item.notes && (
          <VuiBox
            mt={0.6} px={1.2} py={0.6}
            display="flex" alignItems="flex-start" gap={0.8}
            sx={{ background: "#f4f8ff", borderRadius: "8px", border: "1px solid #d8e6f7" }}
          >
            <span style={{ fontSize: FS.sm, marginTop: 1 }}>💬</span>
            <VuiTypography variant="caption" sx={{ color: TC.secondary,  fontSize: "0.7rem", fontStyle: "italic", lineHeight: 1.5 }}>
              {item.notes}
            </VuiTypography>
          </VuiBox>
        )}

        {/* Meta badges */}
        <VuiBox display="flex" gap={0.6} mt={0.8} flexWrap="wrap" alignItems="center">
          <SourceBadge source={item.source} />
          <StatusBadge status={item.confirmedStatus} />
        </VuiBox>
      </VuiBox>

      {/* Action buttons */}
      <VuiBox display="flex" alignItems="center" gap={0.3} flexShrink={0} mt={0.2}>
        {isSaving ? (
          <CircularProgress size={16} sx={{ color: TC.secondary, mx: 0.5 }} />
        ) : (
          <>
            {/* Confirm */}
            {!isConfirmed && (
              <Tooltip title={isDismissed ? "Re-confirm as related party" : "Confirm as related party"}>
                <IconButton size="small" onClick={() => onConfirm(item)}
                  sx={{ color: "#66bb6a", "&:hover": { background: "rgba(76,175,80,0.12)" }, p: "4px" }}>
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {/* Dismiss */}
            {!isDismissed && (
              <Tooltip title="Dismiss — not a related party">
                <IconButton size="small" onClick={() => onDismiss(item)}
                  sx={{ color: "#9e9e9e", "&:hover": { background: "rgba(158,158,158,0.1)" }, p: "4px" }}>
                  <CancelIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {/* Re-open if dismissed */}
            {isDismissed && isConfirmed === false && (
              <Tooltip title="Mark as pending review">
                <IconButton size="small" onClick={() => onConfirm(item)}
                  sx={{ color: "#66bb6a", "&:hover": { background: "rgba(76,175,80,0.12)" }, p: "4px" }}>
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {/* Edit notes */}
            <Tooltip title="Edit notes">
              <IconButton size="small" onClick={() => onEditNotes(item)}
                sx={{ color: TC.secondary, "&:hover": { background: "#f4f8ff", color: TC.midtone }, p: "4px" }}>
                <EditIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            {/* Delete (manual only) */}
            {isManual && (
              <Tooltip title="Delete (manual entry only)">
                <IconButton size="small" onClick={() => onDelete(item)}
                  sx={{ color: "rgba(244,67,54,0.55)", "&:hover": { background: "rgba(244,67,54,0.08)", color: "#ef5350" }, p: "4px" }}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </VuiBox>
    </VuiBox>
  );
}

// ─── Sub-section block (e.g. "Directors", "PSCs") ─────────────────────────────

function SubSection({ groupLabel, items, actionProps }) {
  if (items.length === 0) return null;
  return (
    <VuiBox mb={1.5}>
      <VuiBox display="flex" alignItems="center" gap={1} mb={0.6} px={0.5}>
        <VuiTypography
          variant="caption" fontWeight="bold"
          sx={{ color: TC.tertiary,  textTransform: "uppercase", letterSpacing: "0.07em", fontSize: "0.6rem", opacity: 0.6 }}
        >
          {groupLabel}
        </VuiTypography>
        <Chip
          label={items.length}
          size="small"
          sx={{ height: 16, fontSize: "0.58rem", bgcolor: "#7a94b0", color: TC.secondary, "& .MuiChip-label": { px: 0.8 } }}
        />
      </VuiBox>
      <VuiBox sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7", borderRadius: "10px", overflow: "hidden" }}>
        {items.map((item) => (
          <RelatedPartyRow key={item.id} item={item} {...actionProps} isSaving={actionProps.savingIds.has(item.id)} />
        ))}
      </VuiBox>
    </VuiBox>
  );
}

// ─── Main section (People / Companies) ────────────────────────────────────────

function MainSection({ title, emoji, groups, items, actionProps }) {
  const totalCount = items.length;
  const pendingCount = items.filter((i) => i.confirmedStatus === "pending").length;

  if (totalCount === 0) return null;

  return (
    <VuiBox mb={3}>
      {/* Section header */}
      <VuiBox
        display="flex" alignItems="center" gap={1.5} mb={1.5} px={0.5}
        pb={1} sx={{ borderBottom: "1px solid #e4eef8" }}
      >
        <span style={{ fontSize: "1.05rem" }}>{emoji}</span>
        <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary,  fontSize: FS.md }}>
          {title}
        </VuiTypography>
        <Chip
          label={totalCount}
          size="small"
          sx={{ height: 20, fontSize: FS.xs, fontWeight: FW.bold, bgcolor: "#0a1628", color: TC.midtone, "& .MuiChip-label": { px: 1 } }}
        />
        {pendingCount > 0 && (
          <Chip
            label={`${pendingCount} pending`}
            size="small"
            sx={{ height: 20, fontSize: "0.62rem", bgcolor: "rgba(255,152,0,0.14)", color: "#ffb74d", border: "1px solid rgba(255,152,0,0.35)", "& .MuiChip-label": { px: 1 } }}
          />
        )}
      </VuiBox>

      {/* Sub-sections */}
      {groups.map(({ key, label }) => {
        const groupItems = items.filter((i) => i.relationshipTypes?.includes(key));
        return <SubSection key={key} groupLabel={label} items={groupItems} actionProps={actionProps} />;
      })}

      {/* Catch-all: items not matched by any defined group */}
      {(() => {
        const allGroupKeys = groups.map((g) => g.key);
        const unmatched = items.filter((i) => !i.relationshipTypes?.some((t) => allGroupKeys.includes(t)));
        return <SubSection groupLabel="Other" items={unmatched} actionProps={actionProps} />;
      })()}
    </VuiBox>
  );
}

// ─── Add Manually dialog ───────────────────────────────────────────────────────

const BLANK_FORM = {
  entityType:        "person",
  name:              "",
  relationshipType:  "close_family",
  closeFamilyOf:     "",
  controlPercent:    "",
  reason:            "",
  notes:             "",
};

function AddManualDialog({ open, onClose, onSave }) {
  const [form, setForm]     = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Auto-switch default relationship type when entityType changes
  const handleEntityTypeChange = (e) => {
    const et = e.target.value;
    setForm((f) => ({
      ...f,
      entityType:       et,
      relationshipType: et === "person" ? "close_family" : "parent_or_controlling",
    }));
  };

  const relOptions = form.entityType === "person" ? PERSON_REL_OPTIONS : COMPANY_REL_OPTIONS;

  // Build a human-readable reason from form fields
  const buildAutoReason = () => {
    const cfg = REL_CONFIG[form.relationshipType];
    const label = cfg?.label || form.relationshipType;
    if (form.relationshipType === "close_family" && form.closeFamilyOf) {
      return `Close family of ${form.closeFamilyOf}`;
    }
    if (form.controlPercent) {
      return `${label} — ${form.controlPercent}% control`;
    }
    return `${label} — manually declared`;
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Name is required."); return; }
    setSaving(true);
    setError("");
    try {
      const reason = form.reason.trim() || buildAutoReason();
      await onSave({
        entityType:        form.entityType,
        name:              form.name.trim(),
        relationshipTypes: [form.relationshipType],
        reason,
        notes:  form.notes.trim() || undefined,
        metadata: {
          closeFamilyOf:  form.closeFamilyOf.trim() || undefined,
          controlPercent: form.controlPercent.trim() || undefined,
        },
      });
      setForm(BLANK_FORM);
      onClose();
    } catch (err) {
      setError(err?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => { setForm(BLANK_FORM); setError(""); onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: {
        background: "#ffffff",
        border: "1px solid #d8e6f7", borderRadius: "16px",
      }}}>
      <DialogTitle sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: FS.base, borderBottom: "1px solid #e4eef8", pb: 1.5 }}>
        ➕ Add Related Party Manually
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>

        {/* Entity type */}
        <TextField
          select label="Type of entity" value={form.entityType} onChange={handleEntityTypeChange}
          fullWidth size="small" variant="outlined" sx={INPUT_SX}
          SelectProps={{ MenuProps: { PaperProps: { sx: { background: "#0a0e23", color: TC.primary } } } }}
        >
          <MenuItem value="person">👤 Person</MenuItem>
          <MenuItem value="company">🏢 Company / Entity</MenuItem>
        </TextField>

        {/* Name */}
        <TextField
          label={form.entityType === "person" ? "Full name" : "Company / entity name"}
          value={form.name} onChange={set("name")}
          fullWidth size="small" variant="outlined" sx={INPUT_SX}
          InputLabelProps={{ shrink: true }}
          placeholder={form.entityType === "person" ? "e.g. Jane Smith" : "e.g. Smith Holdings Ltd"}
        />

        {/* Relationship */}
        <TextField
          select label="Relationship to client" value={form.relationshipType} onChange={set("relationshipType")}
          fullWidth size="small" variant="outlined" sx={INPUT_SX}
          SelectProps={{ MenuProps: { PaperProps: { sx: { background: "#0a0e23", color: TC.primary } } } }}
        >
          {relOptions.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {REL_CONFIG[value]?.icon || ""} {label}
            </MenuItem>
          ))}
        </TextField>

        {/* Close family of */}
        {form.relationshipType === "close_family" && (
          <TextField
            label="Close family of (director name)" value={form.closeFamilyOf} onChange={set("closeFamilyOf")}
            fullWidth size="small" variant="outlined" sx={INPUT_SX}
            InputLabelProps={{ shrink: true }}
            placeholder="e.g. John Smith"
            helperText={<span style={{ color: TC.secondary, fontSize: "0.68rem" }}>Spouse, civil partner, child, or financial dependant</span>}
          />
        )}

        {/* Control % (for company relationships) */}
        {["parent_or_controlling", "director_controlled", "informal_control", "subsidiary_of"].includes(form.relationshipType) && (
          <TextField
            label="Control / ownership % (optional)" value={form.controlPercent} onChange={set("controlPercent")}
            fullWidth size="small" variant="outlined" sx={INPUT_SX}
            InputLabelProps={{ shrink: true }}
            placeholder="e.g. 75"
            type="number"
          />
        )}

        <Divider sx={{ bordercolor: "#7a94b0" }} />

        {/* Reason */}
        <TextField
          label="Reason (auto-generated if blank)" value={form.reason} onChange={set("reason")}
          fullWidth size="small" variant="outlined" sx={INPUT_SX}
          InputLabelProps={{ shrink: true }}
          placeholder={buildAutoReason()}
        />

        {/* Notes */}
        <TextField
          label="Notes (optional)" value={form.notes} onChange={set("notes")}
          fullWidth size="small" variant="outlined" sx={INPUT_SX}
          InputLabelProps={{ shrink: true }}
          multiline minRows={2} maxRows={6}
          placeholder="Any additional context for the audit trail…"
        />

        {error && (
          <VuiTypography variant="caption" sx={{ color: TC.error, fontSize: "0.78rem" }}>{error}</VuiTypography>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: "1px solid #e4eef8", p: 2, gap: 1 }}>
        <VuiButton size="small" color="text" variant="outlined" onClick={handleClose} disabled={saving}>Cancel</VuiButton>
        <VuiButton size="small" color="info" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={12} color="inherit" /> : <AddIcon sx={{ fontSize: "13px !important" }} />}>
          {saving ? "Saving…" : "Add Related Party"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Edit notes dialog ─────────────────────────────────────────────────────────

function EditNotesDialog({ open, item, onClose, onSave }) {
  const [notes, setNotes]   = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  useEffect(() => { if (item) setNotes(item.notes || ""); }, [item]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await onSave(item, notes.trim() || null);
      onClose();
    } catch (err) {
      setError(err?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { background: "#ffffff", border: "1px solid #d8e6f7", borderRadius: "16px" } }}>
      <DialogTitle sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: FS.base, borderBottom: "1px solid #e4eef8", pb: 1.5 }}>
        💬 Notes — {item?.name}
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: FS.sm, display: "block", mb: 1.5, opacity: 0.7 }}>
          Notes are saved to the audit trail and visible to all accountants on this firm.
        </VuiTypography>
        <TextField
          value={notes} onChange={(e) => setNotes(e.target.value)}
          fullWidth variant="outlined" sx={INPUT_SX}
          InputLabelProps={{ shrink: true }}
          multiline minRows={4} maxRows={10}
          placeholder="Add any notes about this relationship — e.g. confirmed verbally with client on 12 Apr 2025…"
        />
        {error && (
          <VuiTypography variant="caption" sx={{ color: TC.error, fontSize: "0.78rem", mt: 1, display: "block" }}>{error}</VuiTypography>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: "1px solid #e4eef8", p: 2, gap: 1 }}>
        <VuiButton size="small" color="text" variant="outlined" onClick={onClose} disabled={saving}>Cancel</VuiButton>
        <VuiButton size="small" color="info" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={12} color="inherit" /> : <EditIcon sx={{ fontSize: "13px !important" }} />}>
          {saving ? "Saving…" : "Save Notes"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Suggestions panel ─────────────────────────────────────────────────────────

const CONFIDENCE_CONFIG = {
  high:   { bg: "rgba(76,175,80,0.14)",   border: "rgba(76,175,80,0.4)",    text: "#81c784", label: "High" },
  medium: { bg: "rgba(255,152,0,0.14)",   border: "rgba(255,152,0,0.45)",   text: "#ffb74d", label: "Medium" },
  low:    { bg: "rgba(120,120,140,0.10)", border: "rgba(120,120,140,0.25)", text: "#9e9e9e", label: "Low" },
};

function ConfidenceBadge({ level }) {
  const c = CONFIDENCE_CONFIG[level] || CONFIDENCE_CONFIG.low;
  return (
    <Chip
      label={c.label}
      size="small"
      sx={{ bgcolor: c.bg, color: c.text, border: `1px solid ${c.border}`, fontSize: "0.6rem", height: 18, fontWeight: FW.bold }}
    />
  );
}

function SuggestionsPanel({ suggestions, onReview, onMerge, savingIds }) {
  const [expanded, setExpanded] = useState(false);

  const pending      = suggestions.filter((s) => s.reviewStatus === "pending");
  const autoConfirmed = suggestions.filter((s) => s.autoConfirmed);
  const all          = [...pending, ...suggestions.filter((s) => s.reviewStatus !== "pending")];

  if (suggestions.length === 0) return null;

  const typeIcon  = (type) => type === "duplicate_person" ? "👤👤" : "🧑‍🤝‍🧑";
  const typeTitle = (s) => {
    const prefix = s.type === "duplicate_person" ? "Possible duplicate" : "Possible family";
    return `${prefix}: ${s.entityAName} / ${s.entityBName}`;
  };

  const levelFromScore = (s) =>
    s.confidenceLevel || (s.confidence >= 0.88 ? "high" : s.confidence >= 0.70 ? "medium" : "low");

  return (
    <VuiBox mb={2.5}>
      {/* Header / toggle */}
      <VuiBox
        display="flex" alignItems="center" gap={1.5} px={1.5} py={1.2}
        onClick={() => setExpanded((v) => !v)}
        sx={{
          background: "rgba(255,235,59,0.07)",
          border: "1px solid rgba(255,235,59,0.22)",
          borderRadius: expanded ? "10px 10px 0 0" : "10px",
          cursor: "pointer",
          "&:hover": { background: "rgba(255,235,59,0.11)" },
        }}
      >
        <span style={{ fontSize: FS.base }}>🤖</span>
        <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary,  fontSize: FS.md, flex: 1 }}>
          {pending.length > 0
            ? `${pending.length} Match Suggestion${pending.length !== 1 ? "s" : ""} — click to review`
            : `AI Suggestions (${suggestions.length})`}
        </VuiTypography>
        {autoConfirmed.length > 0 && (
          <Chip label={`${autoConfirmed.length} auto-confirmed`} size="small"
            sx={{ height: 18, fontSize: "0.6rem", fontWeight: FW.bold, bgcolor: "rgba(76,175,80,0.15)", color: "#81c784", border: "1px solid rgba(76,175,80,0.3)", "& .MuiChip-label": { px: 0.8 } }} />
        )}
        {pending.length > 0 && (
          <Chip label={pending.length} size="small"
            sx={{ height: 18, fontSize: "0.62rem", fontWeight: FW.bold, bgcolor: "rgba(255,235,59,0.2)", color: "#fff176", "& .MuiChip-label": { px: 0.8 } }} />
        )}
        <span style={{ color: TC.secondary, fontSize: FS.sm }}>{expanded ? "▲" : "▼"}</span>
      </VuiBox>

      {expanded && (
        <VuiBox sx={{
          background: "#f4f8ff",
          border: "1px solid rgba(255,235,59,0.12)",
          borderTop: "none",
          borderRadius: "0 0 10px 10px",
          overflow: "hidden",
        }}>
          {all.length === 0 ? (
            <VuiBox px={2} py={1.5}>
              <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.73rem", opacity: 0.6 }}>No suggestions available.</VuiTypography>
            </VuiBox>
          ) : (
            all.map((s) => {
              const isReviewed = s.reviewStatus !== "pending";
              const isSaving   = savingIds?.has(s.id);
              const hasAI      = (s.llmScore > 0) || (s.embeddingScore > 0);
              const canMerge   = s.type === "duplicate_person" && s.reviewStatus === "confirmed" && onMerge;
              return (
                <VuiBox
                  key={s.id}
                  display="flex" alignItems="flex-start" gap={1.5} px={2} py={1.5}
                  sx={{
                    borderBottom: "1px solid #e4eef8",
                    opacity: isReviewed && !s.autoConfirmed ? 0.5 : 1,
                    "&:last-child": { borderBottom: "none" },
                  }}
                >
                  {/* Type icon */}
                  <VuiBox sx={{
                    width: 32, height: 32, borderRadius: "8px", flexShrink: 0,
                    background: "rgba(255,235,59,0.1)", border: "1px solid rgba(255,235,59,0.2)",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: FS.md,
                  }}>
                    {typeIcon(s.type)}
                  </VuiBox>

                  {/* Content */}
                  <VuiBox flex={1} minWidth={0}>
                    <VuiTypography variant="button" fontWeight="bold"
                      sx={{ color: TC.primary,  fontSize: "0.8rem", lineHeight: 1.3, display: "block", mb: 0.4 }}>
                      {typeTitle(s)}
                    </VuiTypography>

                    {/* Confidence + review status + AI badges */}
                    <VuiBox display="flex" gap={0.6} mb={0.5} flexWrap="wrap" alignItems="center">
                      <ConfidenceBadge level={levelFromScore(s)} />
                      {hasAI && (
                        <Chip label="🤖 AI" size="small"
                          sx={{ height: 18, fontSize: "0.6rem", fontWeight: FW.bold,
                            bgcolor: "rgba(103,58,183,0.18)", color: "#ce93d8",
                            border: "1px solid rgba(103,58,183,0.35)", "& .MuiChip-label": { px: 0.8 } }} />
                      )}
                      {s.autoConfirmed && (
                        <Chip label="⚡ Auto-confirmed" size="small"
                          sx={{ height: 18, fontSize: "0.6rem", fontWeight: FW.bold,
                            bgcolor: "rgba(76,175,80,0.15)", color: "#81c784",
                            border: "1px solid rgba(76,175,80,0.3)", "& .MuiChip-label": { px: 0.8 } }} />
                      )}
                      {isReviewed && !s.autoConfirmed && (
                        <Chip
                          label={s.reviewStatus === "confirmed" ? "✓ Confirmed" : "✗ Dismissed"}
                          size="small"
                          sx={{
                            bgcolor: s.reviewStatus === "confirmed" ? "rgba(76,175,80,0.12)" : "rgba(120,120,140,0.1)",
                            color:   s.reviewStatus === "confirmed" ? "#81c784"               : "#9e9e9e",
                            border: `1px solid ${s.reviewStatus === "confirmed" ? "rgba(76,175,80,0.3)" : "rgba(120,120,140,0.2)"}`,
                            fontSize: "0.6rem", height: 18,
                          }}
                        />
                      )}
                    </VuiBox>

                    {/* AI reasoning callout — shown when Gemini provided a reason */}
                    {s.llmReason && (
                      <VuiBox mb={0.5} px={1} py={0.6}
                        sx={{ background: "rgba(103,58,183,0.12)", border: "1px solid rgba(103,58,183,0.25)", borderRadius: "6px" }}>
                        <VuiTypography variant="caption" sx={{ fontSize: "0.68rem", color: "#ce93d8", lineHeight: 1.5, display: "block" }}>
                          🤖 {s.llmReason}
                        </VuiTypography>
                      </VuiBox>
                    )}

                    {/* Triggered rule labels (collapsed if llmReason already shown) */}
                    {!s.llmReason && s.triggeredRules?.length > 0 ? (
                      <VuiBox mb={0.3}>
                        {s.triggeredRules.map((r) => (
                          <VuiTypography key={r.rule} variant="caption"
                            sx={{ color: TC.tertiary,  fontSize: "0.68rem", opacity: 0.7, display: "block", lineHeight: 1.5 }}>
                            • {r.label}
                          </VuiTypography>
                        ))}
                      </VuiBox>
                    ) : !s.llmReason && s.reason ? (
                      <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.68rem", opacity: 0.7, display: "block" }}>
                        {s.reason}
                      </VuiTypography>
                    ) : null}
                  </VuiBox>

                  {/* Action buttons */}
                  <VuiBox display="flex" alignItems="center" gap={0.3} flexShrink={0}>
                    {isSaving ? (
                      <CircularProgress size={14} sx={{ color: TC.secondary }} />
                    ) : !isReviewed ? (
                      <>
                        <Tooltip title="Confirm — these are the same / related person">
                          <IconButton size="small" onClick={() => onReview(s.id, "confirmed")}
                            sx={{ color: "#66bb6a", "&:hover": { background: "rgba(76,175,80,0.12)" }, p: "4px" }}>
                            <CheckCircleIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Dismiss — not related">
                          <IconButton size="small" onClick={() => onReview(s.id, "dismissed")}
                            sx={{ color: "#9e9e9e", "&:hover": { background: "rgba(158,158,158,0.1)" }, p: "4px" }}>
                            <CancelIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <VuiBox display="flex" gap={0.3}>
                        {canMerge && (
                          <Tooltip title="Merge records — combine these into one entry">
                            <IconButton size="small" onClick={() => onMerge(s)}
                              sx={{ color: TC.link, "&:hover": { background: "rgba(0,117,255,0.12)" }, p: "4px" }}>
                              <MergeTypeIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Reopen for review">
                          <IconButton size="small" onClick={() => onReview(s.id, "pending")}
                            sx={{ color: TC.tertiary, "&:hover": { color: TC.secondary }, p: "4px" }}>
                            <RefreshIcon sx={{ fontSize: FS.base }} />
                          </IconButton>
                        </Tooltip>
                      </VuiBox>
                    )}
                  </VuiBox>
                </VuiBox>
              );
            })
          )}
        </VuiBox>
      )}
    </VuiBox>
  );
}

// ─── Summary stats bar ─────────────────────────────────────────────────────────
function SummaryBar({ items }) {
  const counts = {
    total:       items.length,
    confirmed:   items.filter((i) => i.confirmedStatus === "confirmed").length,
    pending:     items.filter((i) => i.confirmedStatus === "pending").length,
    dismissed:   items.filter((i) => i.confirmedStatus === "dismissed").length,
    people:      items.filter((i) => i.entityType === "person").length,
    companies:   items.filter((i) => i.entityType === "company").length,
    crossClient: items.filter((i) => i.metadata?.isKnownClient).length,
  };

  if (counts.total === 0) return null;

  const stats = [
    { label: "Total",       value: counts.total,       color: TC.midtone },
    { label: "Confirmed",   value: counts.confirmed,   color: "#66bb6a" },
    { label: "Pending",     value: counts.pending,     color: "#ffb74d" },
    { label: "People",      value: counts.people,      color: TC.link },
    { label: "Companies",   value: counts.companies,   color: "#80cbc4" },
    ...(counts.crossClient > 0 ? [{ label: "⭐ Cross-client", value: counts.crossClient, color: "#fff176" }] : []),
  ];

  return (
    <VuiBox
      display="flex" gap={0} mb={2.5}
      sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7", borderRadius: "10px", overflow: "hidden" }}
    >
      {stats.map(({ label, value, color }, idx) => (
        <VuiBox
          key={label} flex={1} py={1.2} px={1}
          display="flex" flexDirection="column" alignItems="center"
          sx={{
            borderRight: idx < stats.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
          }}
        >
          <VuiTypography variant="h6" sx={{ color, fontWeight: 800, fontSize: "1.05rem", lineHeight: 1 }}>
            {value}
          </VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.6rem", opacity: 0.6, mt: 0.3 }}>
            {label}
          </VuiTypography>
        </VuiBox>
      ))}
    </VuiBox>
  );
}

// ─── Main exported component ───────────────────────────────────────────────────

export default function RelatedPartiesTab({ tenantId, clientId, hasChNumber, clientName }) {
  const [items, setItems]         = useState([]);
  const confirm = useConfirm();
  const [loading, setLoading]     = useState(true);
  const [building, setBuilding]   = useState(false);
  const [error, setError]         = useState("");
  const [buildMsg, setBuildMsg]   = useState("");
  const [savingIds, setSavingIds] = useState(new Set());

  const [suggestions, setSuggestions]               = useState([]);
  const [savingSugIds, setSavingSugIds]              = useState(new Set());

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [notesDialog, setNotesDialog]     = useState({ open: false, item: null });
  const [mergeDialog, setMergeDialog]     = useState({ open: false, suggestion: null, merging: false });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { relatedParties } = await listRelatedParties(tenantId, clientId);
      setItems(relatedParties);
    } catch (err) {
      setError(err?.message || "Failed to load related parties.");
    } finally {
      setLoading(false);
    }
  }, [tenantId, clientId]);

  const loadSuggestions = useCallback(async () => {
    try {
      const data = await listRelatedPartySuggestions(tenantId, clientId);
      setSuggestions(data?.suggestions || []);
    } catch {
      // Non-fatal — suggestions panel is optional
    }
  }, [tenantId, clientId]);

  useEffect(() => {
    load();
    loadSuggestions();
  }, [load, loadSuggestions]);

  const handleBuild = async () => {
    setBuilding(true);
    setBuildMsg("");
    setError("");
    try {
      const result = await buildRelatedPartyGraph(tenantId, clientId);
      const rp = result.relatedParties || [];
      setItems(rp);
      await loadSuggestions();
      const pending = rp.filter((i) => i.confirmedStatus === "pending").length;
      setBuildMsg(
        `Build complete — ${rp.length} related part${rp.length === 1 ? "y" : "ies"} detected` +
        (pending > 0 ? `, ${pending} pending your review.` : ".")
      );
    } catch (err) {
      setError(err?.message || "Build failed. Check the Companies House number is correct.");
    } finally {
      setBuilding(false);
    }
  };

  const markSaving = (id, val) =>
    setSavingIds((prev) => { const s = new Set(prev); val ? s.add(id) : s.delete(id); return s; });

  const handleConfirm = async (item) => {
    markSaving(item.id, true);
    try {
      const updated = await updateRelatedParty(tenantId, clientId, item.id, { confirmedStatus: "confirmed" });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, ...updated } : i)));
    } catch (err) {
      setError(err?.message || "Failed to confirm.");
    } finally {
      markSaving(item.id, false);
    }
  };

  const handleDismiss = async (item) => {
    markSaving(item.id, true);
    try {
      const updated = await updateRelatedParty(tenantId, clientId, item.id, { confirmedStatus: "dismissed" });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, ...updated } : i)));
    } catch (err) {
      setError(err?.message || "Failed to dismiss.");
    } finally {
      markSaving(item.id, false);
    }
  };

  const handleDelete = async (item) => {
    const ok = await confirm({
      title: `Delete "${item.name}"`,
      message: `Remove "${item.name}" from related parties?\nThis cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    markSaving(item.id, true);
    try {
      await deleteRelatedParty(tenantId, clientId, item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (err) {
      setError(err?.message || "Failed to delete.");
    } finally {
      markSaving(item.id, false);
    }
  };

  const handleEditNotes = (item) => setNotesDialog({ open: true, item });

  const handleSaveNotes = async (item, notes) => {
    const updated = await updateRelatedParty(tenantId, clientId, item.id, { notes });
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, notes: updated.notes } : i)));
  };

  const handleAddManual = async (payload) => {
    const newItem = await addManualRelatedParty(tenantId, clientId, payload);
    setItems((prev) => [...prev, newItem]);
  };

  const markSugSaving = (id, val) =>
    setSavingSugIds((prev) => { const s = new Set(prev); val ? s.add(id) : s.delete(id); return s; });

  const handleSuggestionReview = async (sugId, reviewStatus) => {
    markSugSaving(sugId, true);
    try {
      const updated = await updateRelatedPartySuggestion(tenantId, clientId, sugId, { reviewStatus });
      setSuggestions((prev) => prev.map((s) => (s.id === sugId ? { ...s, ...updated } : s)));
    } catch (err) {
      setError(err?.message || "Failed to update suggestion.");
    } finally {
      markSugSaving(sugId, false);
    }
  };

  const handleMergeRequest = (suggestion) => {
    setMergeDialog({ open: true, suggestion, merging: false });
  };

  const handleMergeConfirm = async () => {
    const { suggestion } = mergeDialog;
    if (!suggestion) return;
    setMergeDialog((prev) => ({ ...prev, merging: true }));
    try {
      await mergeRelatedParties(tenantId, clientId, suggestion.entityAId, suggestion.entityBId);
      await Promise.all([load(), loadSuggestions()]);
      setMergeDialog({ open: false, suggestion: null, merging: false });
    } catch (err) {
      setError(err?.message || "Merge failed.");
      setMergeDialog((prev) => ({ ...prev, merging: false }));
    }
  };

  const people    = items.filter((i) => i.entityType === "person");
  const companies = items.filter((i) => i.entityType === "company");

  const actionProps = { onConfirm: handleConfirm, onDismiss: handleDismiss, onDelete: handleDelete, onEditNotes: handleEditNotes, savingIds };

  return (
    <VuiBox>

      {/* ── Toolbar ──────────────────────────────────────────────────────────── */}
      <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: FS.sm, opacity: 0.7, maxWidth: 480, lineHeight: 1.5 }}>
          Related parties for FRS 102 disclosure. Auto-detected from Companies House data, or add manually.
        </VuiTypography>
        <VuiBox display="flex" gap={1} flexWrap="wrap">
          <VuiButton
            size="small" color="text" variant="outlined"
            onClick={() => setAddDialogOpen(true)}
            startIcon={<AddIcon sx={{ fontSize: "13px !important" }} />}
            sx={{ fontSize: FS.sm }}
          >
            Add Manually
          </VuiButton>
          {hasChNumber && (
            <VuiButton
              size="small" color="info"
              onClick={handleBuild}
              disabled={building}
              startIcon={building
                ? <CircularProgress size={12} color="inherit" />
                : <RefreshIcon sx={{ fontSize: "14px !important" }} />}
              sx={{ fontSize: FS.sm }}
            >
              {building
                ? "Building…"
                : items.length === 0
                  ? "Build from Companies House"
                  : "Refresh from Companies House"}
            </VuiButton>
          )}
        </VuiBox>
      </VuiBox>

      {/* ── Status messages ───────────────────────────────────────────────────── */}
      {buildMsg && (
        <VuiBox mb={2} px={1.5} py={1} display="flex" alignItems="center" gap={1}
          sx={{ background: "rgba(76,175,80,0.08)", border: "1px solid rgba(76,175,80,0.22)", borderRadius: "8px" }}>
          <span style={{ fontSize: FS.md }}>✅</span>
          <VuiTypography variant="caption" sx={{ color: "#81c784", fontSize: "0.78rem" }}>{buildMsg}</VuiTypography>
        </VuiBox>
      )}
      {error && (
        <VuiBox mb={2} px={1.5} py={1} display="flex" alignItems="center" gap={1}
          sx={{ background: "rgba(244,67,54,0.08)", border: "1px solid rgba(244,67,54,0.22)", borderRadius: "8px" }}>
          <span style={{ fontSize: FS.md }}>❌</span>
          <VuiTypography variant="caption" sx={{ color: "#ef5350", fontSize: "0.78rem" }}>{error}</VuiTypography>
        </VuiBox>
      )}

      {/* ── No CH number hint ─────────────────────────────────────────────────── */}
      {!hasChNumber && items.length === 0 && !loading && (
        <VuiBox px={2} py={2} sx={{ background: "#f4f8ff", borderRadius: "10px", border: "1px solid #d8e6f7" }}>
          <VuiTypography variant="caption" sx={{ color: TC.secondary,  lineHeight: 1.6 }}>
            ℹ️ Add a Companies House registration number to this client profile to auto-detect related parties. Or use <strong style={{ color: TC.primary }}>Add Manually</strong> to declare close family, shadow directors, or overseas entities.
          </VuiTypography>
        </VuiBox>
      )}

      {/* ── Main content ──────────────────────────────────────────────────────── */}
      {loading ? (
        <LoadingSpinner size="sm" label="Loading related parties…" centered={false} />
      ) : (
        <>
          <SuggestionsPanel
            suggestions={suggestions}
            onReview={handleSuggestionReview}
            onMerge={handleMergeRequest}
            savingIds={savingSugIds}
          />

          <SummaryBar items={items} />

          <MainSection
            title="People"
            emoji="👥"
            groups={PEOPLE_GROUPS}
            items={people}
            actionProps={actionProps}
          />

          <MainSection
            title="Companies & Entities"
            emoji="🏢"
            groups={COMPANY_GROUPS}
            items={companies}
            actionProps={actionProps}
          />

          {/* Empty state — build not run yet */}
          {items.length === 0 && hasChNumber && !loading && (
            <VuiBox
              display="flex" flexDirection="column" alignItems="center" py={4} gap={1.5}
              sx={{ background: "#f4f8ff", borderRadius: "12px", border: "1px solid #d8e6f7" }}
            >
              <span style={{ fontSize: "2rem" }}>🔍</span>
              <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary,  fontSize: "0.9rem" }}>
                No related parties detected yet
              </VuiTypography>
              <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: FS.sm, opacity: 0.7, textAlign: "center", maxWidth: 380 }}>
                Click <strong style={{ color: TC.primary }}>Build from Companies House</strong> to automatically import directors, PSCs, and related companies. You can then confirm, dismiss, or add your own.
              </VuiTypography>
              <VuiButton size="small" color="info" onClick={handleBuild} disabled={building}
                startIcon={building ? <CircularProgress size={12} color="inherit" /> : <RefreshIcon sx={{ fontSize: "14px !important" }} />}
                sx={{ mt: 0.5, fontSize: FS.sm }}>
                {building ? "Building…" : "Build from Companies House"}
              </VuiButton>
            </VuiBox>
          )}
        </>
      )}

      {/* ── Dialogs ───────────────────────────────────────────────────────────── */}
      <AddManualDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSave={handleAddManual}
      />
      <EditNotesDialog
        open={notesDialog.open}
        item={notesDialog.item}
        onClose={() => setNotesDialog({ open: false, item: null })}
        onSave={handleSaveNotes}
      />

      {/* ── Merge confirmation dialog ─────────────────────────────────────────── */}
      <Dialog
        open={mergeDialog.open}
        onClose={() => !mergeDialog.merging && setMergeDialog({ open: false, suggestion: null, merging: false })}
        PaperProps={{ sx: {
          background: "#ffffff",
          border: "1px solid #d8e6f7", borderRadius: "16px",
        }}}
        maxWidth="xs" fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <VuiTypography variant="h6" fontWeight="bold" sx={{ color: TC.primary,  fontSize: "0.95rem" }}>
            <MergeTypeIcon sx={{ fontSize: 18, mr: 0.8, verticalAlign: "middle", color: TC.link }} />
            Merge Duplicate Records
          </VuiTypography>
        </DialogTitle>
        <DialogContent>
          <VuiTypography variant="body2" sx={{ color: TC.secondary,  fontSize: "0.8rem", lineHeight: 1.6, mb: 1 }}>
            This will combine <strong style={{ color: TC.primary }}>{mergeDialog.suggestion?.entityAName}</strong> and{" "}
            <strong style={{ color: TC.primary }}>{mergeDialog.suggestion?.entityBName}</strong> into a single record, merging all their relationships.
          </VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.tertiary,  fontSize: "0.73rem", opacity: 0.6, lineHeight: 1.5 }}>
            ⚠️ This action cannot be undone. The first record will be kept as the canonical entry.
          </VuiTypography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <VuiButton size="small" color="text" variant="outlined"
            disabled={mergeDialog.merging}
            onClick={() => setMergeDialog({ open: false, suggestion: null, merging: false })}
            sx={{ fontSize: FS.sm }}>
            Cancel
          </VuiButton>
          <VuiButton size="small" variant="contained" color="info"
            disabled={mergeDialog.merging}
            onClick={handleMergeConfirm}
            startIcon={mergeDialog.merging ? <CircularProgress size={12} /> : <MergeTypeIcon sx={{ fontSize: "13px !important" }} />}
            sx={{ fontSize: FS.sm }}>
            {mergeDialog.merging ? "Merging…" : "Merge Records"}
          </VuiButton>
        </DialogActions>
      </Dialog>
    </VuiBox>
  );
}


